import { action, computed, observable, runInAction } from 'mobx';
import { find, isEmpty } from 'lodash-es';
import {
	bookingDictApi,
	CarTypesData,
	CitiesData,
} from 'AppClient/api/BookingExternalDictionaries';
import { CountriesData } from 'app-types';

import { FetchStatus } from 'servicesCommon/apiTypes';
import { RootStore } from 'AppClient/stores';

export interface CacheStructure {
	[countryId: string]: CitiesData[];
}
export interface DividedCarsTypes {
	carsTypes: CarTypesData[];
	otherCarsTypes: CarTypesData[];
}

export class BookingDictionariesStore {
	@observable public carTypesDictionary: CarTypesData[] = [];
	@observable private _originCarTypesDictionary: CarTypesData[] = [];
	@observable public citiesDictionary: CitiesData[] = [];
	@observable private _countriesDictionary: CountriesData[] = [];
	@observable public fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private fetchCityStatus: FetchStatus = FetchStatus.IDLE;
	@observable public isPassengerRatioValid: boolean = false;

	constructor(private rootStore: RootStore) {
		this.retrieveAllDictionary();
	}

	@computed
	get countriesDictionary() {
		return this._countriesDictionary;
	}

	@computed
	get isSuccess() {
		return this.fetchStatus === FetchStatus.SUCCESS;
	}

	@computed
	get isCityFetching() {
		return this.fetchCityStatus === FetchStatus.PENDING;
	}

	@computed
	get getDividedCars(): DividedCarsTypes | null {
		const { carTypesDictionary } = this;
		if (isEmpty(carTypesDictionary)) {
			return null;
		}
		const otherCarsTypes = [...carTypesDictionary];
		const carsTypes = otherCarsTypes.splice(0, 3);
		return {
			carsTypes,
			otherCarsTypes,
		};
	}

	@computed
	get isCityLoading() {
		return this.fetchCityStatus === FetchStatus.PENDING;
	}

	@action.bound
	private updateStatus(status: FetchStatus) {
		this.fetchStatus = status;
	}

	@action.bound
	filterByPassengerQuote(passengerCapacity: number) {
		const clone: CarTypesData[] = [...this._originCarTypesDictionary];
		this.carTypesDictionary = clone.filter((car: CarTypesData) => {
			return car.passengerCapacity >= passengerCapacity;
		});
	}

	@action.bound
	passengersRatio(carTypeId: string | number | null, passengerCapacity: number) {
		if (carTypeId) {
			const findCar = find(this._originCarTypesDictionary, {
				carTypeId,
			});
			if (findCar) {
				this.isPassengerRatioValid = findCar.passengerCapacity >= passengerCapacity;
				return findCar.passengerCapacity < passengerCapacity;
			}
		}
		this.isPassengerRatioValid = true;
		return false;
	}

	private retrieveAllDictionary() {
		this.updateStatus(FetchStatus.PENDING);
		Promise.all([bookingDictApi.retrieveCountries(), bookingDictApi.retrieveCarTypes()])
			.then(response => {
				const [countries, cars] = response;
				runInAction(() => {
					this._countriesDictionary = countries.data.result || [];
					this.carTypesDictionary = cars.data.result || [];
					this._originCarTypesDictionary = cars.data.result || [];
					this.updateStatus(FetchStatus.SUCCESS);
				});
			})
			.catch(error => {
				this.rootStore.alertStore.setRequestError(error);
				this.updateStatus(FetchStatus.FAILURE);
			});
	}

	findCarById = (carTypeId?: string | number | null) => {
		if (!carTypeId) {
			return null;
		}
		return find(this.carTypesDictionary, { carTypeId });
	};
}
